import type { ColorMode, NoteKind, ThemeId } from './types'

/**
 * Theme tokens: single source of truth for CSS custom properties (web) and
 * contrast tests (CI). Values from docs/research/06-art-direction.md §4–5.
 * Fills never carry small text; *-ink tokens must pass 4.5:1 on surface and raised.
 */
export interface ThemeTokens {
  surface: string
  surfaceRaised: string
  ink: string
  inkMuted: string
  /** Fill for large shapes and UI; never behind small text except as `buttonFill`. */
  accent: string
  accentInk: string
  /** Text color used on top of `buttonFill` (≥ 4.5:1). */
  onAccent: string
  /**
   * Fill for primary buttons and anything else carrying small text on the
   * accent color. It is `accent` when `onAccent` passes 4.5:1 on it, and
   * `accentInk` otherwise: the Galley's tomato (#E0482F) reaches only 4.09:1
   * with white and 3.99:1 with its ink, so Galley buttons use tomato-ink.
   */
  buttonFill: string
  accent2: string
  accent2Ink: string
  line: string
  decor: string[]
  grainOpacity: number
}

export interface ThemeDef {
  id: ThemeId
  name: { en: string; es: string }
  tone: { en: string; es: string }
  day: ThemeTokens
  night: ThemeTokens
  /** Spine / cover colors paired with a passing text color. */
  covers: Array<{ fill: string; text: string }>
}

/** Night grain inverts to light specks at about 60% of the day opacity (06 §4.4). */
const NIGHT_GRAIN = 0.036

export const THEMES: Record<ThemeId, ThemeDef> = {
  // 06 §5.4: cream enamel by day, chalkboard by night.
  galley: {
    id: 'galley',
    name: { en: 'The Galley', es: 'La Cocina' },
    tone: { en: 'Playful and warm.', es: 'Divertida y cálida.' },
    day: {
      surface: '#FFF6E8',
      surfaceRaised: '#FFFDF8',
      ink: '#231F20',
      inkMuted: '#5A514B',
      accent: '#E0482F',
      accentInk: '#B3321D',
      onAccent: '#FFFFFF',
      buttonFill: '#B3321D',
      accent2: '#9ED9C4',
      accent2Ink: '#1D6B57',
      line: '#8C7F74',
      // butter, mint enamel, checker cream (low contrast on purpose)
      decor: ['#F6C744', '#9ED9C4', '#F1E6D3'],
      grainOpacity: 0.06,
    },
    night: {
      surface: '#1F2A26',
      surfaceRaised: '#2A3732',
      ink: '#F4F1EA',
      inkMuted: '#C4CCC6',
      accent: '#E0482F',
      accentInk: '#FF9A80',
      onAccent: '#1F2A26',
      buttonFill: '#FF9A80',
      accent2: '#9ED9C4',
      accent2Ink: '#9ED9C4',
      line: '#6F7D77',
      // butter, mint, and a chalkboard checker one step lighter than the wall
      // (not in 06; kept at 1.4:1 so the floor stays quiet at night too)
      decor: ['#F6D56B', '#9ED9C4', '#34423D'],
      grainOpacity: NIGHT_GRAIN,
    },
    covers: [
      { fill: '#B3321D', text: '#FFFDF8' },
      { fill: '#1D6B57', text: '#FFFDF8' },
      { fill: '#5A514B', text: '#FFFDF8' },
      { fill: '#9ED9C4', text: '#231F20' },
      { fill: '#F6C744', text: '#231F20' },
    ],
  },
  // 06 §5.1: parchment by day, "after hours" by night.
  stacks: {
    id: 'stacks',
    name: { en: 'The Stacks', es: 'La Biblioteca' },
    tone: { en: 'Quiet and bookish.', es: 'Tranquila y llena de libros.' },
    day: {
      surface: '#F3EAD8',
      surfaceRaised: '#FFFBF2',
      ink: '#2A2118',
      inkMuted: '#5C4E3F',
      accent: '#3D5588',
      accentInk: '#3D5588',
      onAccent: '#FFFFFF',
      buttonFill: '#3D5588',
      accent2: '#F15060',
      accent2Ink: '#A8283A',
      line: '#8C7A64',
      // wood, dark wood, brass, lamp green
      decor: ['#8B5A36', '#6E4428', '#BB8B41', '#407060'],
      grainOpacity: 0.06,
    },
    night: {
      surface: '#1D1A21',
      surfaceRaised: '#2A2530',
      ink: '#F1E8D8',
      inkMuted: '#C7B9A5',
      accent: '#6F86C4',
      accentInk: '#A9B9EC',
      onAccent: '#1D1A21',
      buttonFill: '#6F86C4',
      accent2: '#F15060',
      accent2Ink: '#FF97A0',
      line: '#7E7466',
      // banker's-lamp glow, then the day woods and lamp green
      decor: ['#F2C14E', '#8B5A36', '#6E4428', '#407060'],
      grainOpacity: NIGHT_GRAIN,
    },
    covers: [
      { fill: '#3D5588', text: '#FFFBF2' },
      { fill: '#435060', text: '#FFFBF2' },
      { fill: '#407060', text: '#FFFBF2' },
      { fill: '#914E72', text: '#FFFBF2' },
      { fill: '#BB8B41', text: '#2A2118' },
    ],
  },
  // 06 §5.2: gallery white by day, "late opening" by night. Almost no texture.
  museum: {
    id: 'museum',
    name: { en: 'The Museum', es: 'El Museo' },
    tone: {
      en: 'Clean and calm; good for older students.',
      es: 'Limpio y sereno; ideal para estudiantes mayores.',
    },
    day: {
      surface: '#EFEDE8',
      surfaceRaised: '#FDFCFA',
      ink: '#1B1B1F',
      inkMuted: '#4F4F55',
      accent: '#00838A',
      accentInk: '#006A70',
      onAccent: '#FFFFFF',
      buttonFill: '#00838A',
      accent2: '#FF48B0',
      accent2Ink: '#A8155F',
      line: '#7B7B80',
      // feature walls: deep teal, oxblood, midnight, ochre
      decor: ['#00767C', '#7A2E3A', '#435060', '#C99A3A'],
      grainOpacity: 0.03,
    },
    night: {
      surface: '#17181C',
      surfaceRaised: '#23252B',
      ink: '#EDEBE6',
      inkMuted: '#B3B1AC',
      accent: '#00A3AB',
      accentInk: '#5CC8CE',
      onAccent: '#17181C',
      buttonFill: '#00A3AB',
      accent2: '#FF48B0',
      accent2Ink: '#FF8FCB',
      line: '#6E6F75',
      decor: ['#00767C', '#7A2E3A', '#435060', '#C99A3A'],
      grainOpacity: 0.018,
    },
    covers: [
      { fill: '#00767C', text: '#FDFCFA' },
      { fill: '#7A2E3A', text: '#FDFCFA' },
      { fill: '#435060', text: '#FDFCFA' },
      { fill: '#C99A3A', text: '#1B1B1F' },
    ],
  },
  // 06 §5.3: glass mist by day, "moonlit" by night.
  conservatory: {
    id: 'conservatory',
    name: { en: 'The Conservatory', es: 'El Invernadero' },
    tone: { en: 'Fresh and growing.', es: 'Fresco y en crecimiento.' },
    day: {
      surface: '#EDF4EC',
      surfaceRaised: '#FBFDF9',
      ink: '#14302A',
      inkMuted: '#3E5951',
      accent: '#2F7D4F',
      accentInk: '#256B42',
      onAccent: '#FFFFFF',
      buttonFill: '#2F7D4F',
      accent2: '#C4623F',
      accent2Ink: '#98431F',
      line: '#6F8A80',
      // Kelly green, bloom, sun, iron, glass
      decor: ['#67B346', '#E0567E', '#F2C14E', '#2E4A42', '#D7E8E0'],
      grainOpacity: 0.06,
    },
    night: {
      surface: '#0F1F1B',
      surfaceRaised: '#1A2F29',
      ink: '#E6F0E8',
      inkMuted: '#A8C1B5',
      accent: '#3FA37A',
      accentInk: '#86D6A2',
      onAccent: '#0F1F1B',
      buttonFill: '#3FA37A',
      accent2: '#C4623F',
      accent2Ink: '#F2A583',
      line: '#5C7A70',
      // fireflies (static by default), then the day greens and bloom
      decor: ['#F6E27A', '#67B346', '#2E4A42', '#E0567E'],
      grainOpacity: NIGHT_GRAIN,
    },
    covers: [
      { fill: '#256B42', text: '#FBFDF9' },
      { fill: '#98431F', text: '#FBFDF9' },
      { fill: '#2E4A42', text: '#FBFDF9' },
      { fill: '#F2C14E', text: '#14302A' },
      { fill: '#D7E8E0', text: '#14302A' },
    ],
  },
}

/**
 * Projector + contrast palette (overrides every theme; 06 §4.3): lights-on
 * paper, near-black ink, no grain, 3 px ink lines. Its accent is the Galley's;
 * tokensFor() swaps in each theme's projector accent ink.
 */
export const PROJECTOR_TOKENS: ThemeTokens = {
  surface: '#FFFDF7',
  surfaceRaised: '#FFFFFF',
  ink: '#111111',
  inkMuted: '#333333',
  accent: '#8A2414',
  accentInk: '#8A2414',
  onAccent: '#FFFFFF',
  buttonFill: '#8A2414',
  // No pastel fills behind text on a projector: the second accent is a dark grey.
  accent2: '#4A4A4A',
  accent2Ink: '#333333',
  line: '#111111',
  decor: ['#6B6B6B', '#9A9A9A', '#C8C8C8'],
  grainOpacity: 0,
}

/** Projector accent inks per theme (06 §4.3): ≥ 8.8:1, for headings ≥ 48 px and non-text. */
export const PROJECTOR_ACCENT_INK: Record<ThemeId, string> = {
  galley: '#8A2414',
  stacks: '#243A73',
  museum: '#004F54',
  conservatory: '#1B5233',
}

/** Text on every sticky note (06 §4.5): ≥ 7:1 on each note fill. */
export const NOTE_INK = '#1F1B16'

/**
 * Constant across themes. Every note always shows its word, icon and shape;
 * color is only a backup cue. Glow / Grow / Wonder are 06 §4.5 set F (worst
 * pairwise ΔE_OK 8.4 under protan, deutan and tritan simulation). See and
 * Think were chosen the same way: within a Look-closely walk (See / Think /
 * Wonder) the worst case is also 8.4, and each has its own border shape.
 */
export const NOTE_STYLES: Record<
  NoteKind,
  { fill: string; border: string; borderStyle: 'solid' | 'dashed' | 'double'; icon: string }
> = {
  glow: { fill: '#FFE38A', border: '#8A6A00', borderStyle: 'solid', icon: 'sparkle' },
  grow: { fill: '#CFE0FF', border: '#0072B2', borderStyle: 'solid', icon: 'sprout' },
  wonder: { fill: '#FFFFFF', border: '#4A4A4A', borderStyle: 'dashed', icon: 'question' },
  see: { fill: '#98E8D0', border: '#1D6B57', borderStyle: 'double', icon: 'eye' },
  think: { fill: '#F8C890', border: '#9A4A0B', borderStyle: 'solid', icon: 'bulb' },
}

export const FOCUS_RING = '#1A4FD6'
export const DANGER_INK = '#B00020'
/**
 * Night-mode twins of the constants above: on the dark night surfaces the
 * day values reach only about 2:1, below WCAG's 3:1 for focus indicators and
 * 4.5:1 for text. Same hues, lightened.
 */
export const FOCUS_RING_NIGHT = '#8FB0FF'
export const DANGER_INK_NIGHT = '#FF8A80'

export function tokensFor(theme: ThemeId, mode: ColorMode): ThemeTokens {
  if (mode === 'day' || mode === 'night') return THEMES[theme][mode]
  const accent = PROJECTOR_ACCENT_INK[theme]
  return { ...PROJECTOR_TOKENS, accent, accentInk: accent, buttonFill: accent }
}

/** WCAG 2.x relative luminance of "#RGB" or "#RRGGBB". */
export function relativeLuminance(hex: string): number {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match?.[1]) throw new TypeError(`Not a hex color: ${hex}`)
  const digits = match[1].length === 3 ? [...match[1]].map((d) => d + d).join('') : match[1]
  const n = parseInt(digits, 16)
  return (
    0.2126 * linear((n >> 16) & 255) + 0.7152 * linear((n >> 8) & 255) + 0.0722 * linear(n & 255)
  )
}

/** sRGB channel (0–255) to linear light. */
function linear(channel: number): number {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** WCAG 2.x relative luminance contrast ratio of two hex colors. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}
